import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { reportService } from '../services/api';
import { useSocket } from '../hooks/useSocket';

const COLORS = ['var(--accent)', '#10b981', '#f59e0b', '#84cc16', '#ef4444'];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await reportService.getDashboard();
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useSocket((event) => {
    if (['session:started', 'session:ended', 'game:played'].includes(event)) load();
  });

  if (loading) return <div style={styles.loading}>Carregando...</div>;

  const weekly = data?.weekly_revenue?.map(d => ({
    date: new Date(`${d.date}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'short' }),
    total: parseFloat(d.total)
  })) || [];

  const byMethod = data?.revenue_by_method?.map(d => ({
    name: methodLabel(d.payment_method),
    value: parseFloat(d.total)
  })) || [];

  return (
    <div>
      <h1 style={styles.title}>Dashboard</h1>
      <p style={styles.sub}>{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>

      {/* CARDS */}
      <div style={styles.cards}>
        <StatCard icon="💰" label="Receita Hoje" value={`R$ ${parseFloat(data?.today_revenue || 0).toFixed(2)}`} color="var(--accent)" />
        <StatCard icon="🖥️" label="Sessões Ativas" value={data?.active_sessions || 0} color="#22c55e" />
        <StatCard icon="✅" label="Máquinas Livres" value={`${data?.available_machines || 0}/${data?.total_machines || 0}`} color="#f59e0b" />
        <StatCard icon="📋" label="Sessões Hoje" value={data?.today_sessions || 0} color="#84cc16" />
      </div>

      {/* CHARTS */}
      <div style={styles.charts}>
        <div style={styles.chart}>
          <h3 style={styles.chartTitle}>Receita — Últimos 7 dias</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weekly}>
              <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 12 }} />
              <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{ background: '#262636', border: 'none', borderRadius: 8, color: '#fff' }}
                formatter={v => [`R$ ${v.toFixed(2)}`, 'Receita']}
              />
              <Bar dataKey="total" fill="var(--accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chart}>
          <h3 style={styles.chartTitle}>Pagamentos por Método</h3>
          {byMethod.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={byMethod} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {byMethod.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => `R$ ${parseFloat(v).toFixed(2)}`} contentStyle={{ background: '#262636', border: 'none', borderRadius: 8, color: '#fff' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.empty}>Nenhum pagamento hoje</div>
          )}
        </div>
      </div>

      {/* TOP CLIENTS */}
      {data?.top_clients?.length > 0 && (
        <div style={styles.topClients}>
          <h3 style={styles.chartTitle}>Top Clientes do Mês</h3>
          <div className="touch-scroll-x"><table style={styles.table}>
            <thead>
              <tr>
                {['#', 'Cliente', 'Total gasto', 'Pagamentos'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.top_clients.map((c, i) => (
                <tr key={i}>
                  <td style={styles.td}>{i + 1}</td>
                  <td style={styles.td}>{c.name}</td>
                  <td style={styles.td}>R$ {parseFloat(c.total).toFixed(2)}</td>
                  <td style={styles.td}>{c.payments}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {data?.top_games?.length > 0 && (
        <div style={{ ...styles.topClients, marginTop: 16 }}>
          <h3 style={styles.chartTitle}>Jogos mais jogados do mês</h3>
          <div className="touch-scroll-x"><table style={styles.table}><thead><tr>{['#','Jogo','Jogadas','Jogadores'].map(h=><th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>{data.top_games.map((g,i)=><tr key={g.id}><td style={styles.td}>{i+1}</td><td style={styles.td}><span style={{display:'flex',alignItems:'center',gap:10}}>{g.icon_url?<img src={g.icon_url} alt="" style={{width:38,height:38,objectFit:'cover',borderRadius:7}}/>:<span>🎮</span>}{g.name}</span></td><td style={styles.td}>{g.plays}</td><td style={styles.td}>{g.players}</td></tr>)}</tbody></table></div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{ ...styles.card, borderTop: `3px solid ${color}` }}>
      <div style={styles.cardIcon}>{icon}</div>
      <div style={{ ...styles.cardValue, color }}>{value}</div>
      <div style={styles.cardLabel}>{label}</div>
    </div>
  );
}

function methodLabel(m) {
  return { cash: 'Dinheiro', pix: 'PIX', card_debit: 'Débito', card_credit: 'Crédito', balance: 'Saldo' }[m] || m;
}

const styles = {
  title: { color: '#fff', fontSize: 24, fontWeight: 700, margin: '0 0 4px' },
  sub: { color: '#6b7280', fontSize: 14, marginBottom: 24 },
  loading: { color: '#6b7280', padding: 40, textAlign: 'center' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 24 },
  card: { background: '#14141f', border: '1px solid #262636', borderRadius: 12, padding: 20 },
  cardIcon: { fontSize: 28, marginBottom: 8 },
  cardValue: { fontSize: 28, fontWeight: 700, marginBottom: 4 },
  cardLabel: { color: '#9ca3af', fontSize: 13 },
  charts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 },
  chart: { background: '#14141f', border: '1px solid #262636', borderRadius: 12, padding: 20 },
  chartTitle: { color: '#e5e7eb', fontSize: 15, fontWeight: 600, marginBottom: 16, marginTop: 0 },
  empty: { color: '#6b7280', textAlign: 'center', padding: 40 },
  topClients: { background: '#14141f', border: '1px solid #262636', borderRadius: 12, padding: 20 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#6b7280', fontSize: 12, fontWeight: 600, textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #262636', textTransform: 'uppercase' },
  td: { color: '#e5e7eb', fontSize: 14, padding: '10px 12px', borderBottom: '1px solid #262636' },
};
